import { useCallback, useEffect, useState } from "react";
import { ToastsStore } from "react-toasts";
import Layouts from "../../Layouts";
import BomPageTitle from "./BomPageTitle";
import BomPageQuoteTable from "./BomPageQuoteTable";
import BomPageBomDetails from "./BomPageBomDetails";
import BomIssueReportModal from "../Home/BomIssueReportModal";
import ObsoletePartsReviewModal from "../Home/ObsoletePartsReviewModal";
import UnmatchedPartsReviewModal from "../Home/UnmatchedPartsReviewModal";
import { OverlayLoader } from "../../Loader/index";
import { getFile, getBomDetails, getColumns, getDigiKeyBomEnrichment } from "../../../actions";
import {
  applySelectedAlternateToLine,
  applySelectedUnmatchedPartToLine,
  applySkippedObsoleteToLine,
  getDigiKeyAlternateSaveFailureMessage,
  getLineReviewKey,
  getSavedAlternateFromResponse,
  getUnmatchedOriginalMpn,
  getUnmatchedViewOptionsCache,
  isObsoleteLine,
  isUnmatchedLine,
  matchesBomLine,
  organizeBomByMountingType,
  preferAlternateWithEnrichment,
  toObsoleteSaveApiSelection,
} from "../../../constants/digiKeyColumns";
import { isRealBomLine } from "../../../constants/quoteQuantities";
import { loadStructuredBomlinesForBom } from "../../../utils/loadStructuredBomlines";
import {
  isIssueRowActionable,
  normalizeIssueReport,
} from "../../../constants/issueReportUtils";
import {
  BOM_ISSUE_REPORT,
  DIGIKEY_SAVE_OBSOLETE_ALTERNATES,
  DIGIKEY_VIEW_OPTIONS,
} from "../../../constants/urls";
import { toaster } from "../../../function";
import { connect } from "react-redux";
import "./bomPageStyle.css";

const BomPage = (props) => {
  const [bomDetails, setBomDetails] = useState([]);
  const [bomLines, setBomLines] = useState();
  const [structuredBomlines, setStructuredBomlines] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [showIssueReportModal, setShowIssueReportModal] = useState(false);
  const [issueReport, setIssueReport] = useState(null);
  const [freightTotal, setFreightTotal] = useState(0);
  const [tariffTotal, setTariffTotal] = useState(0);
  const [showObsoletePartsModal, setShowObsoletePartsModal] = useState(false);
  const [obsoletePartsFocusKey, setObsoletePartsFocusKey] = useState("");
  const [showUnmatchedPartsModal, setShowUnmatchedPartsModal] = useState(false);
  const [unmatchedModalLine, setUnmatchedModalLine] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingPercent, setLoadingPercent] = useState(0);

  const { match, user, columns, getBomDetails, getFile, getDigiKeyBomEnrichment, getColumns } = props;
  const bomId = match.params.id;

  useEffect(() => {
    if (!columns?.length && user.token) {
      getColumns(user.token);
    }
  }, [columns, getColumns, user.token]);

  useEffect(() => {
    const getBomFile = async () => {
      setTableLoading(true);
      setStructuredBomlines(null);

      const loaded = await loadStructuredBomlinesForBom({
        token: user.token,
        bomId,
        getBomDetails,
        getFile,
        getDigiKeyBomEnrichment,
      });

      setBomDetails(loaded.bomDetails);
      setIssueReport(normalizeIssueReport(loaded.bomDetails?.data?.issue_report));
      setFreightTotal(loaded.freightTotal);
      setTariffTotal(loaded.tariffTotal);
      setStructuredBomlines(loaded.structuredBomlines);
      setTableLoading(false);
    };

    getBomFile();
  }, [bomId, user.token, getBomDetails, getFile, getDigiKeyBomEnrichment]);

  const openIssueReport = async () => {
    setShowIssueReportModal(true);
    if (!bomId || !user.token) {
      return;
    }
    try {
      const res = await fetch(BOM_ISSUE_REPORT(bomId), {
        method: "POST",
        headers: {
          authorization: "Bearer " + user.token,
        },
      });
      const body = await res.json();
      if (body && body.code >= 200 && body.code < 400 && body.data) {
        setIssueReport(normalizeIssueReport(body.data));
      }
    } catch (e) {
      // Keep the stored snapshot if refresh fails.
    }
  };

  const openObsoletePartsReviewModal = useCallback((line) => {
    const focusLine = line || (structuredBomlines || []).find(isObsoleteLine);
    if (!focusLine) {
      return;
    }
    setObsoletePartsFocusKey(getLineReviewKey(focusLine));
    setShowObsoletePartsModal(true);
  }, [structuredBomlines]);

  const closeObsoletePartsReviewModal = useCallback(() => {
    setShowObsoletePartsModal(false);
    setObsoletePartsFocusKey("");
  }, []);

  const openUnmatchedPartsReviewModal = useCallback(async (line) => {
    if (!isUnmatchedLine(line)) {
      return;
    }
    const current =
      (structuredBomlines || []).find((row) => matchesBomLine(row, line)) || line;
    const count = getUnmatchedViewOptionsCache(current).length;
    if (count > 0) {
      setUnmatchedModalLine(current);
      setShowUnmatchedPartsModal(true);
      return;
    }

    const mpn = getUnmatchedOriginalMpn(current);
    if (!mpn || !user.token) {
      toaster(ToastsStore, "error", `${mpn || "This part"} has 0 suggested parts.`);
      return;
    }

    setIsLoading(true);
    setLoadingMessage("Loading suggested parts…");
    setLoadingPercent(20);
    try {
      const res = await fetch(
        `${DIGIKEY_VIEW_OPTIONS}?part_number=${encodeURIComponent(mpn)}`,
        {
          headers: {
            Accept: "application/json",
            authorization: `Bearer ${user.token}`,
          },
        }
      );
      const json = await res.json();
      const options = json?.data?.options || json?.data?.substitutes || [];
      const loaded = Array.isArray(options) ? options : [];
      const patched = (structuredBomlines || []).map((row) =>
        matchesBomLine(row, current)
          ? {
              ...row,
              substitutes: loaded,
              substitutes_loaded: true,
              alternate_count: loaded.length,
            }
          : row
      );
      setStructuredBomlines(organizeBomByMountingType(patched));
      const nextLine = patched.find((row) => matchesBomLine(row, current)) || {
        ...current,
        substitutes: loaded,
        substitutes_loaded: true,
        alternate_count: loaded.length,
      };
      if (!loaded.length) {
        toaster(
          ToastsStore,
          "error",
          `${mpn || "This part"} has 0 suggested parts.`
        );
        return;
      }
      setUnmatchedModalLine(nextLine);
      setShowUnmatchedPartsModal(true);
    } catch (error) {
      toaster(ToastsStore, "error", "Failed to load suggested parts.");
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
      setLoadingPercent(0);
    }
  }, [structuredBomlines, user.token]);

  const closeUnmatchedPartsReviewModal = useCallback(() => {
    setShowUnmatchedPartsModal(false);
    setUnmatchedModalLine(null);
  }, []);

  const onObsoletePartsSaved = useCallback(async (selections) => {
    const token = user.token;
    const enrichmentProgressToken = `obs-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;

    const apiSelections = selections.map((item) => toObsoleteSaveApiSelection(item));

    let saveResponse = null;

    try {
      const res = await fetch(DIGIKEY_SAVE_OBSOLETE_ALTERNATES, {
        method: "POST",
        headers: {
          authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selections: apiSelections,
          bom_id: bomId || null,
          enrichment_progress_token: enrichmentProgressToken,
        }),
      });
      saveResponse = await res.json();
    } catch (error) {
      toaster(ToastsStore, "error", "Failed to save obsolete alternates.");
      throw new Error("Failed to save obsolete alternates.");
    }

    const failureMessage = getDigiKeyAlternateSaveFailureMessage(
      saveResponse,
      "Failed to save obsolete alternates."
    );
    if (failureMessage) {
      toaster(ToastsStore, "error", failureMessage);
      throw new Error(failureMessage);
    }

    let nextLines = structuredBomlines || [];
    selections.forEach(({ line, alternate, skipped }) => {
      nextLines = nextLines.map((row) => {
        if (!matchesBomLine(row, line)) {
          return row;
        }
        if (skipped) {
          return applySkippedObsoleteToLine({
            ...row,
            substitutes:
              Array.isArray(line.substitutes) && line.substitutes.length
                ? line.substitutes
                : row.substitutes,
          });
        }
        const savedAlternate = getSavedAlternateFromResponse(
          saveResponse,
          alternate,
          {
            part_id: line.part_id,
            obsolete_part_number:
              line.obsolete_part_number || line.manufacturer_part_number,
          }
        );
        const preferredAlternate = preferAlternateWithEnrichment(
          savedAlternate,
          alternate
        );
        return applySelectedAlternateToLine(
          {
            ...row,
            substitutes:
              Array.isArray(line.substitutes) && line.substitutes.length
                ? line.substitutes
                : row.substitutes,
          },
          preferredAlternate
        );
      });
    });

    nextLines = organizeBomByMountingType(nextLines);
    setStructuredBomlines(nextLines);

    const nextIssueReport =
      (saveResponse && saveResponse.data && saveResponse.data.issue_report) ||
      issueReport;
    if (nextIssueReport) {
      setIssueReport(normalizeIssueReport(nextIssueReport));
    }

    toaster(
      ToastsStore,
      "success",
      selections.every((item) => item.skipped)
        ? "Obsolete part(s) skipped. They will not be quoted."
        : "Obsolete alternates saved."
    );
  }, [user.token, bomId, structuredBomlines, issueReport]);

  const persistUnmatchedSelection = useCallback(async (line, alternate) => {
    const token = user.token;
    try {
      const res = await fetch(DIGIKEY_SAVE_OBSOLETE_ALTERNATES, {
        method: "POST",
        headers: {
          authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selections: [
            {
              obsolete_part_id: line.part_id || null,
              obsolete_part_number:
                line.unmatched_original_mpn || line.manufacturer_part_number,
              manufacturer: line.manufacturer || "",
              selection_type: "unmatched",
              alternate,
            },
          ],
          bom_id: bomId || null,
        }),
      });
      const saveResponse = await res.json();
      const failureMessage = getDigiKeyAlternateSaveFailureMessage(
        saveResponse,
        "Failed to save suggested part selection."
      );
      if (failureMessage) {
        toaster(ToastsStore, "error", failureMessage);
        return;
      }
      const nextIssueReport =
        saveResponse && saveResponse.data && saveResponse.data.issue_report;
      if (nextIssueReport) {
        setIssueReport(normalizeIssueReport(nextIssueReport));
      }
    } catch (error) {
      toaster(ToastsStore, "error", "Failed to save suggested part selection.");
    }
  }, [user.token, bomId]);

  const onUnmatchedPartsSaved = useCallback(({ line, alternate, options }) => {
    const preferredAlternate = preferAlternateWithEnrichment(alternate, alternate);
    const nextLines = organizeBomByMountingType(
      (structuredBomlines || []).map((row) =>
        matchesBomLine(row, line)
          ? applySelectedUnmatchedPartToLine(row, preferredAlternate, options)
          : row
      )
    );
    setStructuredBomlines(nextLines);
    closeUnmatchedPartsReviewModal();
    toaster(ToastsStore, "success", "Suggested part saved.");
    persistUnmatchedSelection(line, alternate);
    return true;
  }, [structuredBomlines, closeUnmatchedPartsReviewModal, persistUnmatchedSelection]);

  const handleIssueReportClick = useCallback((issue) => {
    const bomlines = structuredBomlines || [];
    const matchLine = bomlines.find(
      (line) =>
        isRealBomLine(line) &&
        String(line.manufacturer_part_number || "")
          .trim()
          .toUpperCase() ===
          String(issue.manufacturer_part_number || "")
            .trim()
            .toUpperCase()
    );

    if (
      issue.issue_type === "needs_alternate_selection" ||
      issue.issue_type === "invalid_mpn"
    ) {
      if (!isIssueRowActionable(issue, matchLine)) {
        toaster(
          ToastsStore,
          "error",
          `${issue.manufacturer_part_number || "This part"} has 0 suggested parts.`
        );
        return;
      }
      setShowIssueReportModal(false);
      if (matchLine && isUnmatchedLine(matchLine)) {
        openUnmatchedPartsReviewModal(matchLine);
      } else if (matchLine && isObsoleteLine(matchLine)) {
        openObsoletePartsReviewModal(matchLine);
      }
      return;
    }

    if (issue.issue_type === "obsolete") {
      if (!isIssueRowActionable(issue, matchLine)) {
        toaster(
          ToastsStore,
          "error",
          `${issue.manufacturer_part_number || "This part"} has 0 suggested parts.`
        );
        return;
      }
      setShowIssueReportModal(false);
      if (matchLine) {
        openObsoletePartsReviewModal(matchLine);
      } else {
        openObsoletePartsReviewModal();
      }
    }
  }, [structuredBomlines, openUnmatchedPartsReviewModal, openObsoletePartsReviewModal]);

  return (
    <Layouts>
      <OverlayLoader
        show={isLoading}
        percent={loadingPercent}
        message={loadingMessage || "Processing data. Please wait."}
      />
      <div className="p-20 bomPageBox">
        <BomPageTitle
          bomDetails={bomDetails}
          issueReport={issueReport}
          structuredBomlines={structuredBomlines}
          columns={columns}
          freightTotal={freightTotal}
          tariffTotal={tariffTotal}
          onOpenIssueReport={openIssueReport}
        />
      </div>
      <BomPageQuoteTable bomDetails={bomDetails}></BomPageQuoteTable>
      <BomPageBomDetails
        bomDetails={bomDetails}
        bomLines={bomLines}
        structuredBomlines={structuredBomlines}
        tableLoading={tableLoading}
        freightTotal={freightTotal}
        tariffTotal={tariffTotal}
        onSelectObsolete={openObsoletePartsReviewModal}
        onSelectUnmatched={openUnmatchedPartsReviewModal}
      ></BomPageBomDetails>
      <ObsoletePartsReviewModal
        show={showObsoletePartsModal}
        bomlines={structuredBomlines || []}
        focusLineKey={obsoletePartsFocusKey}
        token={user.token}
        onSave={onObsoletePartsSaved}
        onClose={closeObsoletePartsReviewModal}
        setLoading={(e) => setIsLoading(!!e)}
        setLoadingMessage={(msg) => setLoadingMessage(msg || "")}
        setLoadingPercent={(p) => setLoadingPercent(Number(p) || 0)}
      />
      <UnmatchedPartsReviewModal
        show={showUnmatchedPartsModal}
        reviewLine={unmatchedModalLine}
        token={user.token}
        onSave={onUnmatchedPartsSaved}
        onClose={closeUnmatchedPartsReviewModal}
      />
      <BomIssueReportModal
        isOpen={showIssueReportModal}
        toggle={() => setShowIssueReportModal(false)}
        issueReport={issueReport}
        bomName={bomDetails?.data?.file_name}
        bomlines={structuredBomlines || []}
        onIssueClick={handleIssueReportClick}
      />
    </Layouts>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  columns: state.column,
});
export default connect(mapStateToProps, {
  getFile,
  getBomDetails,
  getDigiKeyBomEnrichment,
  getColumns,
})(BomPage);
